import React from "react";
import { Link } from "react-router-dom";
import { Section, Eyebrow, Heading, Lede } from "../components/site/Section";
import { ArrowRight } from "lucide-react";
import { ART, SITE } from "../lib/content";

const TRANSFORMATIONS = [
  { from: "Knowledge", to: "Healing" },
  { from: "Truth", to: "Resistance" },
  { from: "Trauma", to: "Empathy" },
  { from: "Power", to: "Responsibility" },
  { from: "Identity", to: "A bridge — not a prison" },
];

const CREED = [
  { a: "When truth is buried,", b: "knowledge becomes light." },
  { a: "When trauma is weaponized,", b: "compassion becomes rebellion." },
  { a: "When cultures are divided,", b: "friendship becomes revolution." },
  { a: "And when humanity remembers itself,", b: "the chains beneath the world begin to break." },
];

export default function MissionPage() {
  return (
    <div data-testid="mission-page">
      {/* Hero */}
      <Section testId="mission-hero" className="pt-12 md:pt-24">
        <Eyebrow>The Mission</Eyebrow>
        <h1
          data-testid="mission-title"
          className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4 max-w-4xl"
        >
          A mythic saga about the{" "}
          <span className="text-[#C5A059] italic font-light">fractured human soul</span>.
        </h1>
        <Lede>
          Chains Beneath Bakhtak is set in a world of nine nations, each shaped by a
          different response to pain. The story explores how truth, trauma, ambition,
          discipline, wonder, and compassion can either divide humanity — or restore it.
        </Lede>
      </Section>

      {/* Meet the Author */}
      <Section testId="meet-the-author" className="pt-0 border-t border-white/5">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-5">
            <div className="border border-[#C5A059]/20 bg-[#0A0A0A]">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="/art/creator_isaiah.jpg"
                  alt="Isaiah M. Willis-Dean"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 20%" }}
                  loading="lazy"
                  data-testid="author-photo"
                />
              </div>
              <div className="px-6 py-5 border-t border-white/5">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059]">
                  Meet the Author
                </div>
                <div className="font-display text-2xl text-[#EAE0D5] mt-1.5">
                  Isaiah M. Willis-Dean
                </div>
                <div className="text-xs text-[#737373] mt-1 leading-relaxed">
                  Creator · Story Architect · Director of Vision
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <Eyebrow>The Voice Behind Bakhtak</Eyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#EAE0D5] mt-4 leading-[1.05]">
              The creator's mission.
            </h2>
            <p className="text-base md:text-lg text-[#A39E99] leading-relaxed mt-6">
              Isaiah M. Willis-Dean is the creator of{" "}
              <span className="text-[#EAE0D5]">Chains Beneath Bakhtak</span>,
              a multi-season Western anime epic built on character-driven storytelling,
              cultural mythology, and long-form emotional arcs.
            </p>
            <p className="text-base text-[#A39E99] leading-relaxed mt-4">
              With a background in narrative design, worldbuilding, and character
              psychology, Isaiah specializes in crafting:
            </p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-[#A39E99]">
              {[
                "Complex protagonists shaped by their homelands",
                "Morally layered antagonists",
                "Multi-season arcs with cinematic escalation",
                "Nation identities and thematic storytelling",
                "Worlds where culture, conflict, and emotion intertwine",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span className="mt-2 w-1 h-1 bg-[#C5A059] shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t border-white/5">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059]">
                Creative Focus
              </div>
              <p className="text-[#A39E99] mt-3 leading-relaxed">
                Isaiah builds stories around{" "}
                <span className="text-[#EAE0D5]">emotional truth</span> — exploring how
                home, heritage, and identity shape every hero and villain in Bakhtak's
                nine nations.
              </p>
            </div>

            <blockquote
              className="mt-8 border-l-2 border-[#C5A059]/50 pl-6 py-1"
              data-testid="author-quote"
            >
              <p className="font-display italic text-xl md:text-2xl text-[#EAE0D5] leading-snug">
                "My mission is to explore the power of inner conflict in a fresh,
                dynamic way — creating stories that are as psychologically thrilling
                as they are spectacular."
              </p>
              <p className="text-sm text-[#A39E99] mt-4 leading-relaxed">
                "Chains Beneath Bakhtak lets me merge emotional depth with high-energy
                worldbuilding — crafting an experience that challenges the heart as
                much as it entertains the eye."
              </p>
              <footer className="mt-5 text-[10px] tracking-[0.3em] uppercase text-[#C5A059]">
                — Isaiah M. Willis-Dean
              </footer>
            </blockquote>
          </div>
        </div>
      </Section>

      {/* The thesis */}
      <Section testId="mission-thesis" className="pt-0 border-t border-white/5">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-5">
            <Eyebrow>Beneath the Fantasy</Eyebrow>
            <Heading>A deeper reality.</Heading>
          </div>
          <div className="md:col-span-7 space-y-5 text-base md:text-lg text-[#A39E99] leading-relaxed">
            <p>
              Cultures become coping mechanisms. Powers become psychological expressions.
              Monsters emerge when suffering is weaponized by systems of control.
            </p>
            <p>
              Through nine heroes from radically different worlds, Bakhtak asks the
              defining question of our time:
            </p>
            <p className="font-display text-2xl md:text-3xl text-[#EAE0D5] italic leading-snug pt-2">
              "Can a divided world become whole — without losing its humanity?"
            </p>
          </div>
        </div>
      </Section>

      {/* The integration */}
      <Section testId="mission-integration" className="pt-0 border-t border-white/5">
        <Eyebrow>The Journey</Eyebrow>
        <Heading className="mb-10 md:mb-14">A story of integration.</Heading>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 border border-white/5">
          {TRANSFORMATIONS.map((t, i) => (
            <li
              key={t.from}
              data-testid={`transformation-${i + 1}`}
              className="bg-[#0A0A0A] p-7 md:p-8 flex flex-col"
            >
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">
                Becomes
              </span>
              <div className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-3 leading-tight">
                {t.from}
              </div>
              <ArrowRight size={16} className="text-[#C5A059] my-3" strokeWidth={1.5} />
              <div
                className="font-display text-2xl md:text-3xl italic font-light leading-tight text-[#C5A059]"
              >
                {t.to}
              </div>
            </li>
          ))}
        </ul>
        <p className="text-[#A39E99] mt-10 max-w-3xl leading-relaxed">
          What begins as a small adventure between three unlikely companions expands into
          a generational struggle to expose the hidden machinery shaping civilization itself.
        </p>
      </Section>

      {/* Not just fantasy */}
      <Section testId="mission-not-fantasy" className="pt-0 border-t border-white/5">
        <div className="bg-[#0A0A0A] border border-[#C5A059]/20 p-8 md:p-14">
          <Eyebrow>The Promise</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl text-[#EAE0D5] mt-4 leading-[1.1]">
            Not just an epic fantasy.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-10 mt-8 md:mt-10">
            {[
              { label: "A psychological map", body: "Each nation as an expression of how a culture responds to pain." },
              { label: "A spiritual allegory", body: "Truth, identity, and integration mapped through symbol." },
              { label: "A living franchise", body: "Designed for audiences to see themselves in a world of living symbols." },
            ].map((b) => (
              <div key={b.label}>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059]">
                  {b.label}
                </div>
                <p className="text-[#A39E99] mt-3 leading-relaxed text-base">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-lg md:text-xl italic text-[#EAE0D5] max-w-3xl leading-relaxed">
            "The parts of humanity that were taught to fear one another may be the very
            parts needed to heal the world."
          </p>
        </div>
      </Section>

      {/* Pitch versions */}
      <Section testId="mission-pitch" className="pt-0 border-t border-white/5">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-[#0A0A0A] border border-white/5 p-7 md:p-10">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059]">
              One-Line Core Mission
            </div>
            <p className="font-display text-xl md:text-2xl italic text-[#EAE0D5] mt-5 leading-snug">
              "To tell a mythic story that transforms the divisions within humanity into a
              path toward truth, integration, and collective liberation."
            </p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 p-7 md:p-10">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059]">
              Studio / Investor Read
            </div>
            <p className="text-sm md:text-base text-[#A39E99] mt-5 leading-relaxed">
              Chains Beneath Bakhtak is a psychologically resonant fantasy franchise that
              uses nine distinct cultures and heroes as symbolic reflections of the human
              condition — delivering an emotionally powerful story about trauma, truth,
              identity, and the reunification of a fractured world.
            </p>
          </div>
        </div>
      </Section>

      {/* Creative north star */}
      <Section testId="mission-northstar" className="pt-0 border-t border-white/5">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>Creative North Star</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl text-[#EAE0D5] mt-6 leading-tight">
            Illuminate truth.
            <br />
            Confront corruption.
            <br />
            <span className="text-[#C5A059] italic font-light">Integrate the fractured self.</span>
            <br />
            Unite the world.
          </h2>
          <p className="text-[#A39E99] mt-8 italic">
            "This is not just a world audiences will watch.
            <br className="hidden md:block" />
            It is a world they will use to understand themselves."
          </p>
        </div>
      </Section>

      {/* The Bakhtak Creed */}
      <Section testId="mission-creed" className="pt-0 border-t border-white/5">
        <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] justify-center mb-10 md:mb-14">
          <span>The Bakhtak Creed</span>
        </div>
        <ol
          className="max-w-2xl mx-auto space-y-8 md:space-y-12"
          data-testid="creed-list"
        >
          {CREED.map((line, i) => (
            <li key={i} className="text-center">
              <p className="font-display text-2xl md:text-3xl text-[#EAE0D5] italic leading-snug">
                {line.a}
              </p>
              <p
                className="font-display text-2xl md:text-3xl italic font-light leading-snug mt-1"
                style={{ color: "#C5A059" }}
              >
                {line.b}
              </p>
            </li>
          ))}
        </ol>
      </Section>

      {/* CTAs */}
      <Section testId="mission-cta" className="pt-0 border-t border-white/5">
        <div className="text-center max-w-2xl mx-auto">
          <Heading>Step into the world.</Heading>
          <p className="text-[#A39E99] mt-5 leading-relaxed">
            The Bakhtak story is told across screenplay, illustration, lore, and
            community. Every reader becomes part of the chain.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/pilot"
              data-testid="mission-cta-pilot"
              className="bg-[#C5A059] text-black px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-[#D4AF68] inline-flex items-center justify-center gap-2"
            >
              Read the Pilot <ArrowRight size={14} />
            </Link>
            <Link
              to="/nations"
              data-testid="mission-cta-nations"
              className="border border-[#C5A059]/40 text-[#C5A059] px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:bg-[#C5A059]/10 inline-flex items-center justify-center"
            >
              Explore the Nations
            </Link>
            <Link
              to="/community"
              data-testid="mission-cta-join"
              className="border border-white/10 text-[#EAE0D5] px-7 py-3.5 text-xs tracking-[0.22em] uppercase hover:border-[#C5A059]/40 inline-flex items-center justify-center"
            >
              Join the Dream
            </Link>
          </div>
        </div>

        {/* Representation / studio contact */}
        <div
          className="mt-16 md:mt-24 max-w-3xl mx-auto bg-[#0A0A0A] border border-[#C5A059]/25 p-7 md:p-10"
          data-testid="mission-representation"
        >
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059] text-center">
            For Representation &amp; Studio Inquiries
          </div>
          <p className="text-center text-[#A39E99] mt-4 leading-relaxed text-sm md:text-base">
            Chains Beneath Bakhtak is an original IP — sole creator, full rights.
            Open to representation, development, and adaptation conversations.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center text-sm">
            <a
              href={`mailto:${SITE.creator.email}`}
              data-testid="mission-contact-email"
              className="link-gold tracking-[0.12em]"
            >
              {SITE.creator.email}
            </a>
            <span className="hidden sm:inline text-[#737373]">·</span>
            <a
              href={`tel:${SITE.creator.phone}`}
              data-testid="mission-contact-phone"
              className="link-gold tracking-[0.12em]"
            >
              {SITE.creator.phone}
            </a>
          </div>
          <p className="mt-5 text-[10px] tracking-[0.22em] uppercase text-[#737373] text-center">
            Isaiah M. Willis-Dean · Creator
          </p>
        </div>
      </Section>
    </div>
  );
}
